import { events } from '@/app/events/events';
import { focusGrid } from '@/app/helpers/focusGrid';
import type { JsRenderCodeCell } from '@/app/quadratic-core-types';
import { atom, getDefaultStore } from 'jotai';
import type { Point } from 'pixi.js';

export enum ContextMenuType {
  Grid = 'grid',
  Table = 'table',
  TableSort = 'table-sort',
  TableColumn = 'table-column',
  Chart = 'chart',
}

export interface ContextMenuState {
  type?: ContextMenuType;
  world?: Point;
  column?: number;
  row?: number;
  table?: JsRenderCodeCell;
  rename?: boolean;
  selectedColumn?: number;
  initialValue?: string;
}

export const defaultContextMenuState: ContextMenuState = {
  type: undefined,
  world: undefined,
  column: undefined,
  row: undefined,
  table: undefined,
  rename: undefined,
  selectedColumn: undefined,
  initialValue: undefined,
};

export const contextMenuAtom = atom<ContextMenuState>(defaultContextMenuState);

let contextMenuListenersInitialized = false;

export const initContextMenuListeners = () => {
  if (contextMenuListenersInitialized) return () => {};
  contextMenuListenersInitialized = true;

  const store = getDefaultStore();

  // Closes the open context menu and returns focus to the grid. If no menu is
  // open this must be a no-op: cursorPosition fires for reasons unrelated to
  // user navigation (e.g. core re-sending the A1 context after a transaction),
  // and stealing focus then would yank the user out of panel inputs like the
  // chart editor.
  const clear = () => {
    if (store.get(contextMenuAtom).type === undefined) return;
    store.set(contextMenuAtom, {});
    focusGrid();
  };

  const set = (options: ContextMenuState) => {
    if (!options.type) {
      clear();
    } else {
      store.set(contextMenuAtom, options);
    }
  };

  events.on('cursorPosition', clear);
  events.on('contextMenu', set);

  return () => {
    events.off('cursorPosition', clear);
    events.off('contextMenu', set);
    contextMenuListenersInitialized = false;
  };
};
